import { z } from "zod"

import { selectableDocumentTypeKeys } from "../../registries.js"

import { portalAcceptedMimeTypes, portalMaxFileBytes } from "../../portal-file-policy.js"
import { portalItemListProblem } from "../../portal-item-answers.js"
import { portalLinkWriteScope } from "../../portal-link-writes.js"
import {
  portalExampleKinds,
  portalItemExampleStatuses,
} from "../../portal-requests.js"
import {
  PortalReminderConfigBodySchema,
  PortalReminderConfigSchema,
  ReminderOverrideSchema,
} from "../../reminder-rules.js"
import { EngagementActionConfigSchema, EngagementActionResponseReadSchema } from "./engagements.js"
import { transactionQuestionAskOnlyMessage } from "../../transaction-questions.js"
import {
  TransactionQuestionCategoryChoiceSchema,
  TransactionQuestionPayloadSchema,
} from "./transaction-questions.js"
import { ItemSignatureInputSchema, PortalItemSignatureSchema } from "./signatures.js"
import { ApiInboxGmailConfirmationSchema } from "./inbox.js"
import {
  PortalAnswerBodyValueSchema,
  PortalAnswerValueSchema,
  PortalDraftValueSchema,
  PortalItemOptionsInputSchema,
  PortalItemShowWhenInputSchema,
  PortalItemShowWhenSchema,
  PortalRequestItemKindSchema,
  checkPortalShowWhen,
} from "./portal-items.js"
export { portalAcceptedMimeTypes, portalMaxFileBytes } from "../../portal-file-policy.js"

export const portalMaxFilesPerDay = 100
export const portalMaxBytesPerDay = 250 * 1024 * 1024
export const portalSessionDays = 30
export const portalHeldRetentionDays = 365

export const portalReservedSlugs = new Set([
  "admin",
  "api",
  "app",
  "assets",
  "auth",
  "billing",
  /* demo.<portal domain> serves the public industry demo, so no firm can
     claim it. See apps/portal/src/demo. */
  "demo",
  "help",
  "login",
  "mail",
  "portal",
  "signin",
  "signup",
  "status",
  "support",
  "www",
])

export const PortalSlugSchema = z
  .string()
  .trim()
  .min(3)
  .max(48)
  .regex(
    /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/,
    "Use lowercase letters, numbers, and single hyphens."
  )
  .refine((slug) => !slug.includes("--"), "Use single hyphens only.")
  .refine(
    (slug) => !portalReservedSlugs.has(slug),
    "That portal name is reserved."
  )

export const PortalAccentColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, "Use a six-digit hex color.")

export const PortalEmailSchema = z.string().trim().email().max(254)

export const PortalFolderSchema = z.object({
  folderId: z.string().uuid(),
  name: z.string(),
  sortOrder: z.number().int(),
  archivedAt: z.string().datetime().nullable(),
  fileCount: z.number().int().nonnegative().optional(),
})

export type PortalFolder = z.infer<typeof PortalFolderSchema>

export const PortalContactSchema = z.object({
  contactId: z.string().uuid(),
  name: z.string().nullable(),
  email: z.string().email(),
  invitedAt: z.string().datetime().nullable(),
  /** An invite waits to go out. `invitedAt` is set once it is sent. */
  invitePending: z.boolean(),
  lastSeenAt: z.string().datetime().nullable(),
  revokedAt: z.string().datetime().nullable(),
})

export type PortalContact = z.infer<typeof PortalContactSchema>

export const portalFirmFileStatuses = [
  "scanning",
  "held",
  "quarantined",
  "processing",
  "processed",
  "failed",
  "needs_credits",
] as const

export const PortalFirmFileSchema = z.object({
  fileId: z.string().uuid(),
  folderId: z.string().uuid().nullable(),
  folderName: z.string().nullable(),
  requestItemId: z.string().uuid().nullable(),
  fileName: z.string(),
  byteSize: z.number().int().nonnegative(),
  contentType: z.string(),
  uploaderEmail: z.string().email().nullable(),
  uploaderName: z.string().nullable(),
  status: z.enum(portalFirmFileStatuses),
  scanDetectionName: z.string().nullable(),
  classifiedType: z.string().nullable(),
  classifiedPeriod: z.string().nullable(),
  classifiedAccountHint: z.string().nullable(),
  classificationConfidence: z.number().min(0).max(1).nullable(),
  classificationStatus: z.enum([
    "unclassified",
    "queued",
    "classified",
    "failed",
    "disabled",
    "capped",
  ]),
  classificationSuggestions: z
    .array(
      z.object({
        requestId: z.string().uuid(),
        itemId: z.string().uuid(),
        score: z.number().min(0).max(1),
      })
    )
    .default([]),
  matchedBy: z.enum(["client", "classifier", "firm"]).nullable(),
  documentId: z.string().uuid().nullable(),
  receivedAt: z.string().datetime(),
  processedAt: z.string().datetime().nullable(),
  /** Set once retention removed the uploaded blob. The row is still the record
   * of what the client sent; the file itself can no longer be downloaded. */
  blobPurgedAt: z.string().datetime().nullable().default(null),
})

export type PortalFirmFile = z.infer<typeof PortalFirmFileSchema>

/* One collapsed row in the history strip. `purgedCount` is how many of the
   period's files no longer have a downloadable blob, so the UI can say so
   instead of offering a download that would fail. */
export const PortalHistoryPeriodSummarySchema = z.object({
  key: z.string().min(1).max(16),
  kind: z.enum(["month", "quarter", "year"]),
  label: z.string(),
  from: z.string().datetime(),
  to: z.string().datetime(),
  fileCount: z.number().int().nonnegative(),
  purgedCount: z.number().int().nonnegative(),
})

export type PortalHistoryPeriodSummaryResponse = z.infer<
  typeof PortalHistoryPeriodSummarySchema
>

/** A file as its client sees it. Folders are the firm's own filing and are
    never sent to the client; the file names the item it was sent for. */
export const PortalClientFileSchema = z.object({
  fileId: z.string().uuid(),
  requestItemId: z.string().uuid().nullable(),
  /** The request item's name, or null for a file sent without one. */
  requestItemName: z.string().nullable().default(null),
  fileName: z.string(),
  byteSize: z.number().int().nonnegative(),
  uploaderEmail: z.string().email().nullable(),
  uploaderName: z.string().nullable(),
  status: z.enum(["received", "processed"]),
  removable: z.boolean(),
  receivedAt: z.string().datetime(),
  blobPurgedAt: z.string().datetime().nullable().default(null),
})

export type PortalClientFile = z.infer<typeof PortalClientFileSchema>

const PortalCopyTextSchema = z.string().trim().max(2000)
const PortalCopyLineSchema = z.string().trim().max(200)

export const PortalClientLinkStyleSchema = z.enum(["name", "code"])

/** One client's own reminder rules. Null means the firm default. */
export const PortalReminderOverrideSchema = ReminderOverrideSchema

export type PortalReminderOverride = z.infer<typeof PortalReminderOverrideSchema>

export const ApiPortalSettingsResponseSchema = z.object({
  configured: z.boolean(),
  enabled: z.boolean(),
  slug: z.string(),
  portalBaseUrl: z.string().url(),
  displayName: z.string(),
  logoUrl: z.string().url().nullable(),
  accentColor: PortalAccentColorSchema,
  slugEditable: z.boolean(),
  folderTemplate: z.array(z.string()),
  /** Null keeps uploads until the client link is deleted. */
  processedRetentionDays: z
    .union([z.literal(90), z.literal(365), z.literal(730)])
    .nullable(),
  reminderConfig: PortalReminderConfigSchema,
  clientLinkStyle: PortalClientLinkStyleSchema.default("name"),
  showNameWithLogo: z.boolean().default(true),
  homeGreeting: z.string().nullable().default(null),
  homeMessage: z.string().nullable().default(null),
  inviteSubject: z.string().nullable().default(null),
  inviteMessage: z.string().nullable().default(null),
  emailFromName: z.string().nullable().default(null),
  emailReplyTo: z.string().nullable().default(null),
  /** What "Anyone with the link can upload" starts as on a new client. */
  linkUploadsDefault: z.boolean().default(true),
})

export type ApiPortalSettingsResponse = z.infer<
  typeof ApiPortalSettingsResponseSchema
>

export const ApiUpdatePortalSettingsBodySchema = z.object({
  enabled: z.boolean().optional(),
  slug: PortalSlugSchema.optional(),
  displayName: z.string().trim().min(1).max(120).optional(),
  accentColor: PortalAccentColorSchema.optional(),
  folderTemplate: z
    .array(z.string().trim().min(1).max(80))
    .min(1)
    .max(20)
    .refine(
      (items) =>
        new Set(items.map((item) => item.toLowerCase())).size === items.length,
      "Folder names must be unique."
    )
    .optional(),
  processedRetentionDays: z
    .union([z.literal(90), z.literal(365), z.literal(730)])
    .nullable()
    .optional(),
  /** The firm default for every client without rules of their own. */
  reminderConfig: PortalReminderConfigBodySchema.optional(),
  clientLinkStyle: PortalClientLinkStyleSchema.optional(),
  showNameWithLogo: z.boolean().optional(),
  /* Null clears the firm's own words and brings back the default. */
  homeGreeting: PortalCopyLineSchema.nullable().optional(),
  homeMessage: PortalCopyTextSchema.nullable().optional(),
  inviteSubject: PortalCopyLineSchema.nullable().optional(),
  inviteMessage: PortalCopyTextSchema.nullable().optional(),
  emailFromName: z.string().trim().max(80).nullable().optional(),
  emailReplyTo: PortalEmailSchema.nullable().optional(),
  linkUploadsDefault: z.boolean().optional(),
  /**
   * The slug was read off the firm's website, not typed. Moving off the
   * address the server made up this way does not use the one change.
   */
  slugFromWebsite: z.boolean().optional(),
})

export const ApiPortalInvitePreviewBodySchema = z.object({
  displayName: z.string().trim().max(120).optional(),
  subject: PortalCopyLineSchema.nullable().optional(),
  message: PortalCopyTextSchema.nullable().optional(),
  fromName: z.string().trim().max(80).nullable().optional(),
  replyTo: z.string().trim().max(254).nullable().optional(),
  /* The contact details at the foot of the email, as the firm types them. */
  supportName: z.string().trim().max(120).nullable().optional(),
  supportEmail: z.email().max(254).nullable().optional(),
  supportPhone: z.string().trim().max(60).nullable().optional(),
})

export type ApiPortalInvitePreviewBody = z.infer<
  typeof ApiPortalInvitePreviewBodySchema
>

export const ApiPortalInvitePreviewResponseSchema = z.object({
  subject: z.string(),
  html: z.string(),
  from: z.string(),
  replyTo: z.string().nullable(),
})

export type ApiPortalInvitePreviewResponse = z.infer<
  typeof ApiPortalInvitePreviewResponseSchema
>

/** A reminder as a client gets it, from the sender the firm is typing. */
export const ApiPortalReminderPreviewBodySchema =
  ApiPortalInvitePreviewBodySchema.pick({
    displayName: true,
    fromName: true,
    replyTo: true,
    supportName: true,
    supportEmail: true,
    supportPhone: true,
  })

export type ApiPortalReminderPreviewBody = z.infer<
  typeof ApiPortalReminderPreviewBodySchema
>

export const ApiPortalClientSlugCheckResponseSchema = z.object({
  slug: z.string(),
  available: z.boolean(),
  /** Why it cannot be used, in words a firm can act on. */
  problem: z.string().nullable(),
})

/** The link a new client would get from a base: the base itself when it is
    free, or the first free base-2, base-3. */
export const ApiPortalClientSlugSuggestResponseSchema = z.object({
  base: z.string(),
  slug: z.string().nullable(),
  problem: z.string().nullable(),
})

export type ApiPortalClientSlugSuggestResponse = z.infer<
  typeof ApiPortalClientSlugSuggestResponseSchema
>

export type ApiPortalClientSlugCheckResponse = z.infer<
  typeof ApiPortalClientSlugCheckResponseSchema
>

export const ApiPortalClientSlugBodySchema = z.object({
  slug: z.string().trim().min(1).max(80),
  notifyContacts: z.boolean().default(false),
})

export type ApiPortalClientSlugBody = z.input<typeof ApiPortalClientSlugBodySchema>

export const ApiPortalOverridesBodySchema = z.object({
  homeMessage: PortalCopyTextSchema.nullable(),
  reminderOverride: PortalReminderOverrideSchema.nullable(),
})

export type ApiPortalOverridesBody = z.infer<typeof ApiPortalOverridesBodySchema>

export type ApiUpdatePortalSettingsBody = z.infer<
  typeof ApiUpdatePortalSettingsBodySchema
>

export const ApiPortalLogoUploadBodySchema = z.object({
  fileName: z.string().trim().min(1).max(200),
  contentType: z.enum(["image/png", "image/jpeg", "image/webp"]),
  byteSize: z
    .number()
    .int()
    .positive()
    .max(2 * 1024 * 1024),
})

export type ApiPortalLogoUploadBody = z.infer<
  typeof ApiPortalLogoUploadBodySchema
>

export const ApiPortalLogoUploadResponseSchema = z.object({
  blobPath: z.string(),
  uploadToken: z.string(),
  uploadUrl: z.string().url(),
  uploadUrlExpiresAt: z.string().datetime(),
  uploadHeaders: z.record(z.string(), z.string()),
})

export const ApiPortalLogoConfirmBodySchema = z.object({
  uploadToken: z.string().min(1),
})

/** What a firm typed in the website field. A bare domain is allowed, so the
    scheme is added by the server rather than demanded of the reader. */
export const ApiPortalBrandLookupBodySchema = z.object({
  website: z.string().trim().min(3).max(255),
})

export type ApiPortalBrandLookupBody = z.infer<
  typeof ApiPortalBrandLookupBodySchema
>

/** An image types the browser can draw on a canvas. SVG is read as well: the
    browser redraws it to PNG before the logo is uploaded, because the logo
    upload takes raster images only. */
export const portalBrandImageTypes = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/svg+xml",
] as const

export const ApiPortalBrandLookupResponseSchema = z.object({
  /** The page that answered, after redirects. */
  siteUrl: z.string().url(),
  /** The site's own name for itself, when it has one. */
  siteName: z.string().nullable(),
  /** The registered name the site states in its structured data, when it
      states one. Optional so older API deployments still parse. */
  legalName: z.string().nullable().optional(),
  /** Inlined so the browser can draw it without a second cross-origin read. */
  logo: z
    .object({
      dataUrl: z.string().min(1),
      contentType: z.enum(portalBrandImageTypes),
      byteSize: z.number().int().positive(),
    })
    .nullable(),
  /** The colors the site declares about itself, best first. */
  colors: z.array(PortalAccentColorSchema).max(6),
})

export type ApiPortalBrandLookupResponse = z.infer<
  typeof ApiPortalBrandLookupResponseSchema
>

export const ApiPortalWorkspaceResponseSchema = z.object({
  enabled: z.boolean(),
  workspaceId: z.string().uuid(),
  workspaceName: z.string(),
  clientCompanyName: z.string().nullable(),
  linkExists: z.boolean(),
  /** The link the firm hands out: /{clientSlug} when the firm uses readable
      links, /c/<token> otherwise. Null while the client has no link. */
  linkUrl: z.string().url().nullable(),
  /** The client's current readable link segment. */
  clientSlug: z.string().nullable().default(null),
  /** This client's own portal message; null uses the firm's. */
  homeMessage: z.string().nullable().default(null),
  reminderOverride: PortalReminderOverrideSchema.nullable().default(null),
  /** The contact reminders greet by name. Null means the first contact that
      still has access. */
  primaryContactId: z.string().uuid().nullable().default(null),
  restrictToListedContacts: z.boolean(),
  /** Anyone with the link can upload and answer open items without
      signing in. */
  linkUploadsEnabled: z.boolean().default(true),
  folders: z.array(PortalFolderSchema),
  contacts: z.array(PortalContactSchema),
  /** The working set only. Processed files past the grace period move to
   * `history`, so this payload stays the same size in year five as in week
   * one. Expand one period with `getPortalWorkspaceHistory`. */
  files: z.array(PortalFirmFileSchema),
  history: z.array(PortalHistoryPeriodSummarySchema),
  recentLinkContacts: z.number().int().nonnegative(),
  analytics: z.object({
    clientMedianRequestCycleHours: z.number().nonnegative().nullable(),
    organizationMedianRequestCycleHours: z.number().nonnegative().nullable(),
    clientCompletedRequestCount: z.number().int().nonnegative(),
    organizationCompletedRequestCount: z.number().int().nonnegative(),
    portalUploadCount: z.number().int().nonnegative(),
    matchedWithoutFirmCount: z.number().int().nonnegative(),
    matchedWithoutFirmPercentage: z.number().min(0).max(100).nullable(),
  }),
})

export type ApiPortalWorkspaceResponse = z.infer<
  typeof ApiPortalWorkspaceResponseSchema
>

export const ApiPortalHistoryPeriodResponseSchema = z.object({
  period: PortalHistoryPeriodSummarySchema,
  files: z.array(PortalFirmFileSchema),
})

export type ApiPortalHistoryPeriodResponse = z.infer<
  typeof ApiPortalHistoryPeriodResponseSchema
>

export const ApiPortalContactBodySchema = z.object({
  sharedAccessConfirmed: z.literal(true),
  name: z.string().trim().min(1).max(120).optional(),
  email: PortalEmailSchema,
  sendInvitation: z.boolean().default(false),
  /** Legacy callers may send this field; the server always builds its own URL. */
  portalUrl: z.string().url().optional(),
})

export type ApiPortalContactBody = z.infer<typeof ApiPortalContactBodySchema>

export const ApiPortalRestrictionBodySchema = z.object({
  restrictToListedContacts: z.boolean(),
})

export const ApiPortalLinkUploadsBodySchema = z.object({
  linkUploadsEnabled: z.boolean(),
})

export const ApiPortalFolderBodySchema = z.object({
  name: z.string().trim().min(1).max(80),
})

export const ApiUpdatePortalFolderBodySchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  archived: z.boolean().optional(),
})

export const ApiReorderPortalFoldersBodySchema = z.object({
  folderIds: z.array(z.string().uuid()).min(1).max(20),
})

export const ApiMovePortalFileBodySchema = z.object({
  folderId: z.string().uuid(),
})

export const PortalPeriodTypeSchema = z.enum(["month", "quarter", "year"])

export const PortalRequestKindSchema = z.enum(["period", "adhoc"])
export const PortalRequestPeriodModeSchema = z.enum([
  "request_period",
  "any",
  "explicit",
])
export const PortalRequestItemStatusSchema = z.enum([
  "open",
  "received",
  "needs_review",
  "accepted",
])

export const PortalRequestAnswerSchema = z.object({
  value: PortalAnswerValueSchema,
  categoryId: z.string().uuid().optional(),
  currency: z.string().length(3).optional(),
  answeredAt: z.string().datetime(),
  answeredBy: z.object({
    kind: z.enum(["contact", "firm", "link"]),
    id: z.string().min(1),
    name: z.string().optional(),
    email: z.string().optional(),
  }),
})

/** An image or file the firm attached to an item to show what it wants.
    Templates and requests share one stored copy. */
export const PortalItemExampleSchema = z.object({
  exampleId: z.string().uuid(),
  kind: z.enum(portalExampleKinds),
  fileName: z.string(),
  /** A client sees an example only once it is `held`. */
  status: z.enum(portalItemExampleStatuses),
})

export type PortalItemExample = z.infer<typeof PortalItemExampleSchema>

/** What the client typed and has not sent. Only the client's own reads
    carry it; the firm sees the answer alone. */
export const PortalRequestItemDraftSchema = z.object({
  value: PortalDraftValueSchema,
  savedAt: z.iso.datetime(),
})

export type PortalRequestItemDraftBody = z.infer<
  typeof PortalRequestItemDraftSchema
>

/** `saved` is false when a newer save got there first; `draft` is what the
    item holds either way. */
export const PortalRequestItemDraftResponseSchema = z.object({
  saved: z.boolean(),
  draft: PortalRequestItemDraftSchema.nullable(),
})

export const PortalRequestItemResponseSchema = z.object({
  itemId: z.string().uuid(),
  templateItemId: z.string().uuid().nullable(),
  kind: PortalRequestItemKindSchema,
  name: z.string(),
  description: z.string().nullable(),
  required: z.boolean(),
  options: z.array(z.string()),
  expectedTypes: z.array(z.string()),
  accountHint: z.string().nullable(),
  periodMode: PortalRequestPeriodModeSchema,
  explicitPeriod: z.string().nullable(),
  allowMultiple: z.boolean(),
  answer: PortalRequestAnswerSchema.nullable(),
  draft: PortalRequestItemDraftSchema.nullable().optional(),
  example: PortalItemExampleSchema.nullable().optional(),
  actionConfig: EngagementActionConfigSchema.nullable().optional(),
  actionResponse: EngagementActionResponseReadSchema.nullable().optional(),
  evidenceVersion: z.number().int().min(1).optional(),
  verifiedVersion: z.number().int().nullable().optional(),
  verifiedBy: z.string().nullable().optional(),
  verifiedAt: z.string().datetime().nullable().optional(),
  waivedReason: z.string().nullable().optional(),
  blockerKind: z.enum(["help", "waiver"]).nullable().optional(),
  blockerReason: z.string().nullable().optional(),
  rejectionReason: z.string().nullable().optional(),
  acceptedAt: z.string().datetime().nullable(),
  acceptedBy: z.string().nullable(),
  sortOrder: z.number().int(),
  status: PortalRequestItemStatusSchema,
  /** Shows the item only after an earlier answer. On a template it names a
      template item. */
  showWhen: PortalItemShowWhenSchema.nullable().optional(),
  question: TransactionQuestionPayloadSchema.nullable().optional(),
  /** The current letter on a signature item. */
  signature: PortalItemSignatureSchema.nullable().optional(),
})

export const PortalRequestResponseSchema = z.object({
  publishedAt: z.iso.datetime().nullable().optional(),
  requestId: z.string().uuid(),
  workspaceId: z.string().uuid(),
  kind: PortalRequestKindSchema,
  title: z.string().nullable(),
  templateId: z.string().uuid().nullable(),
  periodType: PortalPeriodTypeSchema.nullable(),
  periodStart: z.string().date().nullable(),
  periodEnd: z.string().date().nullable(),
  periodLabel: z.string(),
  dueAt: z.string().datetime().nullable(),
  engagementId: z.string().uuid().nullable().optional(),
  coverageStart: z.string().date().nullable().optional(),
  coverageEnd: z.string().date().nullable().optional(),
  remindersPausedAt: z.string().datetime().nullable().optional(),
  cancelledAt: z.string().datetime().nullable().optional(),
  clientDoneAt: z.string().datetime().nullable(),
  clientDoneNote: z.string().nullable(),
  closedAt: z.string().datetime().nullable(),
  progress: z.object({
    received: z.number().int().nonnegative(),
    accepted: z.number().int().nonnegative(),
    required: z.number().int().nonnegative(),
  }),
  complete: z.boolean(),
  overdue: z.boolean(),
  items: z.array(PortalRequestItemResponseSchema),
  files: z.array(PortalFirmFileSchema).optional(),
  unfiledFiles: z.array(PortalFirmFileSchema).optional(),
  /** Present when the request holds a transaction question. */
  categoryChoices: z.array(TransactionQuestionCategoryChoiceSchema).optional(),
})

export type PortalRequestResponse = z.infer<typeof PortalRequestResponseSchema>

/* An item's fields with no defaults. A change to one item is built from
   these, so a field the firm did not send stays as it is. */
const PortalRequestItemFieldsSchema = z.object({
  templateItemId: z.string().uuid().nullable().optional(),
  kind: PortalRequestItemKindSchema,
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).nullable().optional(),
  required: z.boolean(),
  options: PortalItemOptionsInputSchema.unwrap(),
  expectedTypes: z
    .array(z.enum(selectableDocumentTypeKeys))
    .max(selectableDocumentTypeKeys.length),
  accountHint: z.string().trim().max(120).nullable().optional(),
  periodMode: PortalRequestPeriodModeSchema,
  explicitPeriod: z
    .string()
    .regex(/^\d{4}-(?:0[1-9]|1[0-2])$/)
    .nullable()
    .optional(),
  allowMultiple: z.boolean(),
  /** An example the firm uploaded; see PortalItemExampleSchema. */
  exampleFileId: z.string().uuid().nullable().optional(),
  signature: ItemSignatureInputSchema.optional(),
  /** Shows the item only after an earlier answer in the same list. */
  showWhen: PortalItemShowWhenInputSchema.nullable().optional(),
})

const PortalRequestItemInputBaseSchema = PortalRequestItemFieldsSchema.extend({
  kind: PortalRequestItemFieldsSchema.shape.kind.default("document"),
  required: PortalRequestItemFieldsSchema.shape.required.default(true),
  options: PortalItemOptionsInputSchema,
  expectedTypes: PortalRequestItemFieldsSchema.shape.expectedTypes.default([]),
  periodMode:
    PortalRequestItemFieldsSchema.shape.periodMode.default("request_period"),
  allowMultiple:
    PortalRequestItemFieldsSchema.shape.allowMultiple.default(false),
})

type ItemCheckInput = z.infer<typeof PortalRequestItemInputBaseSchema>

/* The checks every request or list item passes, whichever schema holds it. */
const checkRequestItem = (item: ItemCheckInput, context: z.RefinementCtx) => {
  if (item.kind === "signature" && !item.signature?.originalToken) {
    context.addIssue({
      code: "custom",
      path: ["signature"],
      message: "Choose the PDF to sign.",
    })
  }
  if (item.kind !== "signature" && item.signature) {
    context.addIssue({
      code: "custom",
      path: ["signature"],
      message: "Only a document to sign can have a PDF to sign.",
    })
  }
  const listProblem = portalItemListProblem(item.kind, item.options)
  if (listProblem) {
    context.addIssue({
      code: "custom",
      path: ["options"],
      message: listProblem,
    })
  }
  if (item.kind !== "document" && item.expectedTypes.length > 0) {
    context.addIssue({
      code: "custom",
      path: ["expectedTypes"],
      message: "Only a document item can name document types.",
    })
  }
  if (item.periodMode === "explicit" && !item.explicitPeriod) {
    context.addIssue({
      code: "custom",
      path: ["explicitPeriod"],
      message: "Choose a month.",
    })
  }
  if (item.kind === "transaction_question") {
    context.addIssue({
      code: "custom",
      path: ["kind"],
      message: transactionQuestionAskOnlyMessage,
    })
  }
}

/* The action an accounting access item or a link to another site takes.
   A link carries its own https URL. Accounting access may come bare: the
   server fills it from the firm's settings (accountingAccessConfigFrom),
   so no client of the API keeps its own copy of the firm's details. */
const checkItemAction = (
  item: {
    kind: string
    actionConfig?: z.infer<typeof EngagementActionConfigSchema> | null
  },
  context: z.RefinementCtx
) => {
  if (
    item.kind === "external_handoff" &&
    item.actionConfig?.type !== "external_handoff"
  )
    context.addIssue({
      code: "custom",
      path: ["actionConfig"],
      message: "Add the link.",
    })
  if (
    item.kind === "external_handoff" &&
    item.actionConfig?.type === "external_handoff" &&
    !item.actionConfig.url.startsWith("https://")
  )
    context.addIssue({
      code: "custom",
      path: ["actionConfig"],
      message: "Use a link that starts with https://",
    })
  if (
    item.actionConfig &&
    item.kind !== "external_handoff" &&
    item.kind !== "accounting_access"
  )
    context.addIssue({
      code: "custom",
      path: ["actionConfig"],
      message: "This item cannot have an action.",
    })
  if (
    item.actionConfig &&
    (item.kind === "external_handoff" || item.kind === "accounting_access") &&
    item.actionConfig.type !== item.kind
  )
    context.addIssue({
      code: "custom",
      path: ["actionConfig"],
      message: "This action does not fit the item.",
    })
}

const PortalRequestItemInputSchema = PortalRequestItemInputBaseSchema.extend({
  actionConfig: EngagementActionConfigSchema.nullable().optional(),
}).superRefine((item, context) => {
  checkRequestItem(item, context)
  checkItemAction(item, context)
})

const PortalRequestItemsInputSchema = z
  .array(PortalRequestItemInputSchema)
  .min(1)
  .max(50)
  .refine(
    (items) =>
      new Set(items.map((item) => item.name.toLowerCase())).size ===
      items.length,
    "Item names must be unique."
  )
  .superRefine(checkPortalShowWhen)

export const ApiCreatePortalRequestBodySchema = z.union([
  z.object({
    kind: z.literal("period").default("period"),
    title: z.null().optional(),
    templateId: z.string().uuid().nullable().optional(),
    periodType: PortalPeriodTypeSchema,
    periodStart: z.string().date(),
    dueAt: z.string().datetime(),
    items: PortalRequestItemsInputSchema,
  }),
  z.object({
    kind: z.literal("adhoc"),
    title: z.string().trim().min(1).max(120),
    templateId: z.string().uuid().nullable().optional(),
    periodType: z.null().optional(),
    periodStart: z.null().optional(),
    dueAt: z.string().datetime(),
    items: PortalRequestItemsInputSchema,
  }),
])

export type ApiCreatePortalRequestBody = z.input<
  typeof ApiCreatePortalRequestBodySchema
>

export const ApiUpdatePortalRequestBodySchema = z.object({
  dueAt: z.string().datetime().optional(),
  closed: z.boolean().optional(),
})

/* One item added or changed on its own has no list to read a condition
   against, so a condition is set only where the whole list is sent. */
export const ApiPortalRequestItemBodySchema =
  PortalRequestItemInputSchema.refine((item) => !item.showWhen, {
    path: ["showWhen"],
    message: "Set this in the list of items, not on one item.",
  })

export type ApiPortalRequestItemBody = z.input<
  typeof ApiPortalRequestItemBodySchema
>

/* A signature item's letter changes through its own route, which makes a new
   version; an item cannot turn into one here. The fields have no defaults,
   so a change to the name alone keeps the kind and the list. The API checks
   the list against the kind the item has after the change. */
export const ApiUpdatePortalRequestItemBodySchema =
  PortalRequestItemFieldsSchema.omit({ signature: true, showWhen: true })
    .partial()
    .extend({
      sortOrder: z.number().int().nonnegative().optional(),
    })
    .refine((item) => item.kind !== "signature", {
      path: ["kind"],
      message: "Add a document to sign as a new item.",
    })
    .refine((item) => item.kind !== "transaction_question", {
      path: ["kind"],
      message: transactionQuestionAskOnlyMessage,
    })

export type ApiUpdatePortalRequestItemBody = z.input<
  typeof ApiUpdatePortalRequestItemBodySchema
>

/** The firm's per-file review of one document item on a regular request.
    Files on the item that are not kept come off it. A note, or any ask-again,
    goes to the client as a message about the request. */
export const ApiReviewPortalRequestItemBodySchema = z.object({
  decision: z.enum(["accept", "ask_again"]),
  keepFileIds: z.array(z.string().uuid()).max(50),
  note: z.string().trim().max(2000).optional(),
})

export type ApiReviewPortalRequestItemBody = z.input<
  typeof ApiReviewPortalRequestItemBodySchema
>

export const PortalRequestItemAnswerBodySchema = z.object({
  value: PortalAnswerBodyValueSchema,
  /** Only on a transaction question: one of the request's categoryChoices. */
  categoryId: z.string().uuid().nullable().optional(),
})

export const PortalTimelineItemSchema = z.object({
  id: z.string().uuid(),
  eventType: z.string(),
  actor: z.object({
    kind: z.enum(["firm_user", "portal_contact", "system"]),
    displayName: z.string().optional(),
  }),
  entity: z.object({ kind: z.string(), id: z.string().optional() }),
  display: z.object({
    title: z.string(),
    detail: z.string().optional(),
    count: z.number().int().optional(),
    linkUrl: z.string().url().optional(),
    participants: z.array(z.string().email()).optional(),
  }),
  createdAt: z.string().datetime(),
})

export const PortalTimelineResponseSchema = z.object({
  items: z.array(PortalTimelineItemSchema),
  nextCursor: z.string().max(200).nullable(),
})

export const PortalThreadAttachmentSchema = z.object({
  fileId: z.string().uuid(),
  fileName: z.string(),
  status: z.string(),
  available: z.boolean(),
})

export const PortalThreadMessageSchema = z.object({
  id: z.string().uuid(),
  authorSide: z.enum(["firm", "client"]),
  body: z.string(),
  tombstoned: z.boolean(),
  attachments: z.array(PortalThreadAttachmentSchema),
  createdAt: z.string().datetime(),
})

export const PortalThreadSummarySchema = z.object({
  id: z.string().uuid(),
  subject: z.string(),
  requestId: z.string().uuid().nullable(),
  /** Set on the thread about one item, titled with the item's name. */
  requestItemId: z.string().uuid().nullable(),
  portalFileId: z.string().uuid().nullable(),
  resolvedAt: z.string().datetime().nullable(),
  messageCount: z.number().int().nonnegative(),
  lastMessageAt: z.string().datetime().nullable(),
  /** Who wrote the newest message. An open thread whose last word is the
      client's waits on the firm's reply. */
  lastAuthorSide: z.enum(["firm", "client"]).nullable(),
  /** The newest message on one line, up to 140 characters. Null when there
      is none or it was removed. */
  lastMessagePreview: z.string().nullable(),
})

export const PortalThreadListResponseSchema = z.object({
  threads: z.array(PortalThreadSummarySchema),
})

export const PortalThreadResponseSchema = z.object({
  thread: PortalThreadSummarySchema,
  messages: z.array(PortalThreadMessageSchema),
})

export const PortalDeliverableSchema = z.object({
  fileId: z.string().uuid(),
  fileName: z.string(),
  byteSize: z.number().int().nonnegative(),
  sharedAt: z.string().datetime(),
  acknowledged: z.boolean(),
  available: z.boolean(),
  /** A staged file the client cannot see yet. Only the firm's list has any. */
  draft: z.boolean().default(false),
  /** The signed copy of a letter. It stays with its signing record and
      cannot be deleted. */
  signedCopy: z.boolean().default(false),
})

export const PortalDeliverablesResponseSchema = z.object({
  files: z.array(PortalDeliverableSchema),
})

export const PortalFileAccessResponseSchema = z.object({
  accessUrl: z.string().url(),
  expiresAt: z.string().datetime(),
  fileName: z.string(),
})

export const PortalCreateThreadBodySchema = z
  .object({
    subject: z.string().trim().min(1).max(160),
    body: z.string().trim().min(1).max(5000),
    requestId: z.string().uuid().nullable().optional(),
    /** A question about one item. It joins the item's open thread when
        there is one; the request is read off the item. */
    requestItemId: z.string().uuid().nullable().optional(),
    portalFileId: z.string().uuid().nullable().optional(),
    attachmentIds: z.array(z.string().uuid()).max(10).default([]),
  })
  .refine(
    (input) => !((input.requestId || input.requestItemId) && input.portalFileId),
    { message: "Choose a request or a file, not both." }
  )

/** The firm marks a thread resolved, or opens it again. */
export const PortalResolveThreadBodySchema = z.object({
  resolved: z.boolean(),
})

export const PortalPostThreadMessageBodySchema = z.object({
  body: z.string().trim().min(1).max(5000),
  attachmentIds: z.array(z.string().uuid()).max(10).default([]),
})

/* A request list's item. It also takes the action an accounting access or
   a link to another site needs. Accounting access is stored bare and filled
   from the firm's settings when it is shown or copied, so a list never keeps
   its own copy of the firm's email or firm ID. */
const PortalRequestListItemInputSchema = PortalRequestItemInputBaseSchema.extend(
  { actionConfig: EngagementActionConfigSchema.nullable().optional() }
).superRefine((item, context) => {
  checkRequestItem(item, context)
  if (item.kind === "signature")
    context.addIssue({
      code: "custom",
      path: ["kind"],
      message: "A request list cannot hold a document to sign.",
    })
  checkItemAction(item, context)
})

export const ApiPortalRequestTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).nullable().optional(),
  kind: PortalRequestKindSchema,
  items: z
    .array(PortalRequestListItemInputSchema)
    .min(1)
    .max(50)
    .refine(
      (items) =>
        new Set(items.map((item) => item.name.toLowerCase())).size ===
        items.length,
      "Item names must be unique."
    )
    .superRefine(checkPortalShowWhen),
})

export type ApiPortalRequestTemplateBody = z.input<
  typeof ApiPortalRequestTemplateBodySchema
>

export const ApiPortalRequestTemplateResponseSchema = z.object({
  templateId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  kind: PortalRequestKindSchema,
  starter: z.boolean(),
  /** The list the add-client wizard picks first. One per firm. */
  isDefault: z.boolean(),
  archivedAt: z.string().datetime().nullable(),
  items: z.array(
    PortalRequestItemResponseSchema.omit({
      answer: true,
      draft: true,
      acceptedAt: true,
      acceptedBy: true,
      status: true,
      signature: true,
    })
  ),
})

export const ApiPortalRequestTemplatesResponseSchema = z.object({
  templates: z.array(ApiPortalRequestTemplateResponseSchema),
})

export type ApiPortalRequestTemplateResponse = z.infer<
  typeof ApiPortalRequestTemplateResponseSchema
>

export type ApiPortalRequestTemplatesResponse = z.infer<
  typeof ApiPortalRequestTemplatesResponseSchema
>

export const ApiArchivePortalRequestTemplateBodySchema = z.object({
  archived: z.boolean(),
})

export const ApiFileAgainstPortalRequestBodySchema = z.object({
  requestItemId: z.string().uuid().nullable(),
})

export const ApiPortalRequestProposalQuerySchema = z.object({
  periodType: PortalPeriodTypeSchema,
  periodStart: z.string().date(),
})

export const ApiPortalRequestProposalResponseSchema = z.object({
  source: z.enum([
    "template",
    "previous_request",
    "processed_documents",
    "folder_templates",
  ]),
  periodType: PortalPeriodTypeSchema,
  periodStart: z.string().date(),
  templateId: z.string().uuid().nullable().optional(),
  // Each item names its example too, so the sheet can show it before sending.
  items: z.array(
    PortalRequestItemInputBaseSchema.extend({
      example: PortalItemExampleSchema.nullable().optional(),
    })
  ),
})

export const ApiPortalOutstandingResponseSchema = z.object({
  requests: z.array(
    z.object({
      requestId: z.string().uuid(),
      workspaceId: z.string().uuid(),
      workspaceName: z.string(),
      clientCompanyName: z.string().nullable(),
      kind: PortalRequestKindSchema,
      title: z.string().nullable(),
      periodType: PortalPeriodTypeSchema.nullable(),
      periodStart: z.string().date().nullable(),
      periodLabel: z.string(),
      dueAt: z.string().datetime(),
      /** When the request went out; the reminder plan counts from it. */
      publishedAt: z.string().datetime().nullable().default(null),
      requiredCount: z.number().int().nonnegative(),
      receivedCount: z.number().int().nonnegative(),
      acceptedCount: z.number().int().nonnegative(),
      needsReviewCount: z.number().int().nonnegative(),
      unmatchedCount: z.number().int().nonnegative(),
      clientDoneAt: z.string().datetime().nullable(),
      /** Set when the firm closed the request. The queue carries a closed
       * request only while the finished work has not gone back to the client,
       * so a row with a `closedAt` is one the firm still owes a delivery. */
      closedAt: z.string().datetime().nullable(),
      /** The last time a firm file reached this client's portal after the
       * request closed; null while nothing has. */
      deliveredAt: z.string().datetime().nullable(),
      lastClientActivityAt: z.string().datetime().nullable(),
      nextScheduledSendAt: z.string().datetime().nullable(),
      overdue: z.boolean(),
    })
  ),
})

export const ApiPromotePortalFilesBodySchema = z.object({
  fileIds: z.array(z.string().uuid()).min(1).max(100),
  documentTypeKey: z.enum(selectableDocumentTypeKeys).optional(),
})

export type ApiPromotePortalFilesBody = z.infer<
  typeof ApiPromotePortalFilesBodySchema
>

export const ApiPromotePortalFilesResponseSchema = z.object({
  promoted: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  results: z.array(
    z.object({
      fileId: z.string().uuid(),
      status: z.enum(["promoted", "failed"]),
      documentId: z.string().uuid().optional(),
      message: z.string().optional(),
    })
  ),
})

export const ApiPortalFileAccessResponseSchema = z.object({
  accessUrl: z.string().url(),
  expiresAt: z.string().datetime(),
})

export const ApiPortalActivityResponseSchema = z.object({
  clients: z.array(
    z.object({
      workspaceId: z.string().uuid(),
      workspaceName: z.string(),
      clientCompanyName: z.string().nullable(),
      heldFiles: z.number().int().nonnegative(),
      lastUploadAt: z.string().datetime().nullable(),
    })
  ),
})

// Public portal lane ---------------------------------------------------------

export const PortalBrandingResponseSchema = z.object({
  supportName: z.string().nullable().optional(),
  supportEmail: z.email().nullable().optional(),
  supportPhone: z.string().nullable().optional(),
  slug: z.string(),
  displayName: z.string(),
  accentColor: PortalAccentColorSchema,
  logoUrl: z.string().url().nullable(),
  poweredBy: z.literal("LedgerBox"),
  showNameWithLogo: z.boolean().optional(),
  /** Raw, with {first_name} and friends still in it; the portal fills them. */
  homeGreeting: z.string().nullable().optional(),
  homeMessage: z.string().nullable().optional(),
})

export type PortalBrandingResponse = z.infer<
  typeof PortalBrandingResponseSchema
>

export const PortalRequestCodeBodySchema = z.object({
  /* A token, or `slug:<client-slug>` for a readable link. */
  linkToken: z.string().min(3).max(256),
  email: PortalEmailSchema,
})

export const PortalRequestCodeResponseSchema = z.object({
  challengeId: z.string().uuid(),
  message: z.string(),
})

export const PortalVerifyCodeBodySchema = z.object({
  /* A token, or `slug:<client-slug>` for a readable link. */
  linkToken: z.string().min(3).max(256),
  challengeId: z.string().uuid(),
  email: PortalEmailSchema,
  code: z.string().regex(/^\d{6}$/),
})

export const PortalSessionResponseSchema = z.object({
  authenticated: z.literal(true),
  contact: z.object({
    /** The signed-in contact, so the portal knows who signs a document. */
    contactId: z.string().uuid(),
    name: z.string().nullable(),
    email: z.string().email(),
  }),
  workspace: z.object({
    name: z.string(),
    clientCompanyName: z.string().nullable(),
  }),
  branding: PortalBrandingResponseSchema,
  uploadAllowed: z.boolean(),
  files: z.array(PortalClientFileSchema),
  history: z.array(PortalHistoryPeriodSummarySchema),
  requests: z.array(PortalRequestResponseSchema),
})

export type PortalSessionResponse = z.infer<typeof PortalSessionResponseSchema>

export const PortalClientHistoryPeriodResponseSchema = z.object({
  period: PortalHistoryPeriodSummarySchema,
  files: z.array(PortalClientFileSchema),
})

export const PortalMintUploadBodySchema = z.object({
  deliveryStaged: z.boolean().optional(),
  folderId: z.string().uuid().nullable().optional(),
  fileName: z.string().trim().min(1).max(240),
  byteSize: z.number().int().positive().max(portalMaxFileBytes),
  contentType: z.enum(portalAcceptedMimeTypes),
  contentHash: z.string().regex(/^(?:sha256:)?[0-9a-fA-F]{64}$/),
})

export type PortalMintUploadBody = z.infer<typeof PortalMintUploadBodySchema>

export const PortalMintUploadResponseSchema = z.object({
  fileId: z.string().uuid(),
  uploadToken: z.string(),
  uploadUrl: z.string().url(),
  uploadUrlExpiresAt: z.string().datetime(),
  uploadHeaders: z.record(z.string(), z.string()),
})

/** The firm's upload of an item example. Examples belong to the firm, not a
    client, so the same file serves every request made from a template. */
export const ApiPortalExampleUploadBodySchema = PortalMintUploadBodySchema.pick({
  fileName: true,
  byteSize: true,
  contentType: true,
  contentHash: true,
})

export const PortalConfirmUploadBodySchema = z.object({
  uploadToken: z.string().min(1),
})

export const PortalConfirmUploadResponseSchema = z.object({
  file: PortalClientFileSchema,
})

export const PortalRequestDoneBodySchema = z.object({
  note: z.string().trim().max(2000).optional(),
})

export const PortalLogoutResponseSchema = z.object({ ok: z.literal(true) })

/* Upload by link --------------------------------------------------------

   A visitor with only the client link holds a write-only grant. Everything
   below is all such a visitor ever reads: the open items as titles and
   instructions. No file names, amounts, earlier answers or messages. */

/** Who a link visitor says they are. Stored, shown to the firm, never
    verified. */
export const PortalLinkUploaderSchema = z.object({
  name: z.string().trim().max(120).optional(),
  email: PortalEmailSchema.optional(),
})

export type PortalLinkUploader = z.infer<typeof PortalLinkUploaderSchema>

export const PortalLinkItemSchema = z.object({
  itemId: z.string().uuid(),
  kind: PortalRequestItemKindSchema,
  name: z.string(),
  description: z.string().nullable(),
  required: z.boolean(),
  /** The choices or to-dos the firm wrote, on the kinds that have them. */
  options: z.array(z.string()),
  allowMultiple: z.boolean(),
  action: z.enum(["upload", "answer"]),
})

export type PortalLinkItem = z.infer<typeof PortalLinkItemSchema>

export const PortalLinkRequestSchema = z.object({
  requestId: z.string().uuid(),
  label: z.string(),
  items: z.array(PortalLinkItemSchema),
})

export const PortalLinkViewResponseSchema = z.object({
  scope: z.literal(portalLinkWriteScope),
  workspace: z.object({
    name: z.string(),
    clientCompanyName: z.string().nullable(),
  }),
  uploadAllowed: z.boolean(),
  requests: z.array(PortalLinkRequestSchema),
  /** Open items only a signed-in contact can finish, by name only: the
      same title the firm wrote, with nothing the client sent. */
  signInItems: z.array(z.object({ itemId: z.string(), name: z.string() })),
})

export type PortalLinkViewResponse = z.infer<
  typeof PortalLinkViewResponseSchema
>

export const PortalLinkMintUploadBodySchema = PortalMintUploadBodySchema.pick({
  fileName: true,
  byteSize: true,
  contentType: true,
  contentHash: true,
}).extend({ uploader: PortalLinkUploaderSchema.optional() })

export type PortalLinkMintUploadBody = z.infer<
  typeof PortalLinkMintUploadBodySchema
>

/** A link upload is confirmed without any file details coming back. */
export const PortalLinkConfirmUploadResponseSchema = z.object({
  received: z.literal(true),
})

export const PortalLinkAnswerBodySchema = z.object({
  value: PortalAnswerValueSchema,
  uploader: PortalLinkUploaderSchema.optional(),
})

export type PortalLinkAnswerBody = z.infer<typeof PortalLinkAnswerBodySchema>

/* The public industry demo -------------------------------------------------

   One unauthenticated call, on the public portal lane. The address is what
   the personalization costs: it buys the brand lookup that opens the demo as
   the prospect's own portal, rather than being a toll on top of it. */

/**
 * The personas the public demo opens on, and what each one is called.
 *
 * This is the contract between three places: the marketing industry pages
 * link to /demo/<slug>, apps/portal must have a profile for every key here
 * (its registry is typed against them), and the API names the demo in the
 * lead mail. `firms` is the /firms landing page rather than an
 * /industries page; the rest are industry slugs.
 */
export const demoIndustries = {
  firms: "Bookkeeping firm",
  accountants: "Accountant or bookkeeper",
  "tax-preparers": "Tax preparer",
  lenders: "Lender",
  "mortgage-lenders": "Mortgage broker or lender",
  "family-law-forensic": "Family law or forensic accounting",
  "property-managers": "Property manager",
  "ecommerce-sellers": "E-commerce seller",
  nonprofits: "Nonprofit or grant manager",
  "real-estate-investors": "Real estate investor",
} as const

export type DemoIndustrySlug = keyof typeof demoIndustries

export const demoIndustrySlugs = Object.keys(
  demoIndustries
) as DemoIndustrySlug[]

export const PortalDemoLeadBodySchema = z.object({
  email: PortalEmailSchema,
  /** Their own site, read for a logo and a color. Omitted means a plain mark. */
  website: z.string().trim().min(3).max(255).optional(),
  /** The demo they opened. */
  industry: z.enum(demoIndustrySlugs as [DemoIndustrySlug, ...DemoIndustrySlug[]]),
})

export type PortalDemoLeadBody = z.infer<typeof PortalDemoLeadBodySchema>

export const PortalDemoLeadResponseSchema = z.object({
  /** Null when no website was given, or when the site gave nothing usable. */
  brand: ApiPortalBrandLookupResponseSchema.nullable(),
})

export type PortalDemoLeadResponse = z.infer<
  typeof PortalDemoLeadResponseSchema
>

export const PortalRetentionSweepResponseSchema = z.object({
  examined: z.number().int().nonnegative(),
  purged: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  authCodesPruned: z.number().int().nonnegative(),
  sessionsPruned: z.number().int().nonnegative(),
  /** Processed files whose blob retention removed this run. Rows survive. */
  blobsPurged: z.number().int().nonnegative().default(0),
})

// Personal linked mail -------------------------------------------------------

export const MailProviderSchema = z.enum(["gmail", "outlook"])
export type MailProvider = z.infer<typeof MailProviderSchema>

export const LinkedMailAccountSchema = z.object({
  id: z.string().uuid(),
  provider: MailProviderSchema,
  emailAddress: z.string().email(),
  lastSyncedAt: z.string().datetime().nullable(),
})

export const LinkedMailAccountsResponseSchema = z.object({
  configured: z.object({ gmail: z.boolean(), outlook: z.boolean() }),
  accounts: z.array(LinkedMailAccountSchema),
})

export const LinkedMailThreadSchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string().uuid().nullable(),
  provider: MailProviderSchema,
  subject: z.string(),
  participants: z.array(z.string().email()),
  snippet: z.string().max(1000),
  lastMessageAt: z.string().datetime(),
  mailboxUrl: z.string().url(),
  candidateWorkspaceIds: z.array(z.string().uuid()),
  ambiguous: z.boolean(),
})

export const LinkedMailThreadsResponseSchema = z.object({
  threads: z.array(LinkedMailThreadSchema),
})

export const LinkMailThreadBodySchema = z.object({
  workspaceId: z.string().uuid(),
})

export const SyncLinkedMailResponseSchema = z.object({
  examined: z.number().int().nonnegative(),
  linked: z.number().int().nonnegative(),
  ambiguous: z.number().int().nonnegative(),
})

/* Email forwarding, as the client sees it -----------------------------------

   The client's own forward-to address, so they can set up a Gmail or Outlook
   rule themselves, and Gmail's forwarding confirmation when one was caught on
   its way to that address. Null when the firm's plan has no forwarding or the
   environment cannot receive mail, so the portal shows nothing rather than an
   address that goes nowhere. */
export const PortalForwardingSchema = z.object({
  address: z.string(),
  gmailConfirmation: ApiInboxGmailConfirmationSchema.nullable(),
})

export type PortalForwarding = z.infer<typeof PortalForwardingSchema>

export const PortalForwardingResponseSchema = z.object({
  forwarding: PortalForwardingSchema.nullable(),
})

export type PortalForwardingResponse = z.infer<
  typeof PortalForwardingResponseSchema
>
